import type { PrescriptionFormData } from '../../hooks/formHooks/usePrescriptionForm';

const BASE = process.env.REACT_APP_API_BASE_URL;

if (!BASE) {
  throw new Error('REACT_APP_API_BASE_URL is not set');
}

export interface ApiError extends Error {
  status: number;
  expired: boolean;
  response: { status: number; data: any };
}

async function apiRequest<T>(path: string, options?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      headers: { 'Content-Type': 'application/json' },
      ...options,
    });
  } catch (networkErr) {
    const err = new Error('Network error — please check your connection.') as ApiError;
    err.status = 0;
    err.expired = false;
    err.response = { status: 0, data: { error: 'Network error' } };
    throw err;
  }

  const text = await res.text();
  const json = text ? JSON.parse(text) : {};

  if (!res.ok) {
    const err = new Error(json.error || `Request failed (${res.status})`) as ApiError;
    err.status = res.status;
    err.expired = json.expired ?? false;
    err.response = { status: res.status, data: json };
    throw err;
  }
  return json as T;
}

export interface DoctorInfo {
  id: string;
  name: string;
  specialization: string;
  regNo: string;
  signatureUrl: string | null;
}

export interface DoctorAccessData {
  doctor: DoctorInfo;
  patients: any[];
  token_minutes_remaining: number;
}

export function getDoctorAccess(
  token: string,
): Promise<{ success: boolean; data: DoctorAccessData }> {
  return apiRequest(`/doctor-access?token=${encodeURIComponent(token)}`);
}

export function submitCase(
  token: string,
  docId: string,
  body: { prescription: PrescriptionFormData },
): Promise<{
  success: boolean;
  data: { doc_id: string; doctor_id: string; submitted_at: string };
  message: string;
}> {
  return apiRequest(
    `/doctor-access/cases/${encodeURIComponent(docId)}/submit?token=${encodeURIComponent(token)}`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}
